import { Injectable } from '@angular/core';
import { Action, State, StateContext, createSelector } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { v4 as uuidv4 } from 'uuid';

import * as actions from './feedback.actions';
import { FeedbackStateModel } from './feedback-state.model';
import { FeedbackEntryModel } from './feedback-entry.model';

function getCombinedFeedbackId(payload) {
  return `${payload.projectId}-${payload.puzzleName}-${payload.featureId}`;
}

// @dynamic
@State<FeedbackStateModel>({
  name: 'feedbackStates',
  defaults: {
    feedbackEntries: {},
  },
})
@Injectable()
export class FeedbackState {
  static getByID(payload: { projectId: string; puzzleName: string; featureId: string }) {
    const feedbackId = getCombinedFeedbackId(payload);
    return createSelector([this], (state: FeedbackStateModel) => {
      if (state.feedbackEntries.hasOwnProperty(feedbackId)) {
        return state.feedbackEntries[feedbackId];
      }
      return null;
    });
  }

  @Action(actions.InitFeedbackEntry)
  InitFeedbackEntry(store: StateContext<FeedbackStateModel>, { payload }: actions.InitFeedbackEntry) {
    const key = getCombinedFeedbackId(payload);
    const state = store.getState();
    if (!(key in state.feedbackEntries)) {
      store.setState(
        patch({
          feedbackEntries: patch({
            [getCombinedFeedbackId(payload)]: {
              UUID: uuidv4(),
              score: undefined,
              text: undefined,
              email: undefined,
            } as FeedbackEntryModel,
          }),
        })
      );
    }
  }

  @Action(actions.SetFeedbackScore)
  SetFeedbackScore(store: StateContext<FeedbackStateModel>, { payload }: actions.SetFeedbackScore) {
    const key = getCombinedFeedbackId(payload);
    const state = store.getState();
    if (!state.feedbackEntries[key]) {
      return;
    }
    if (state.feedbackEntries[key].score) {
      return;
    }
    store.setState(
      patch({
        feedbackEntries: patch({
          [key]: patch({
            score: payload.score,
          }),
        }),
      })
    );
  }

  /*
   * Overwrites the context on every prompt: the entry survives in local storage, so a second
   * round would otherwise be reported with the numbers of the first one.
   */
  @Action(actions.SetFeedbackContext)
  SetFeedbackContext(store: StateContext<FeedbackStateModel>, { payload }: actions.SetFeedbackContext) {
    const key = getCombinedFeedbackId(payload);
    const state = store.getState();
    if (!state.feedbackEntries[key]) {
      return;
    }
    store.setState(
      patch({
        feedbackEntries: patch({
          [key]: patch({
            context: payload.context,
          }),
        }),
      })
    );
  }

  @Action(actions.SetFeedbackText)
  SetFeedbackText(store: StateContext<FeedbackStateModel>, { payload }: actions.SetFeedbackText) {
    const key = getCombinedFeedbackId(payload);
    const state = store.getState();
    if (!state.feedbackEntries[key]) {
      return;
    }
    store.setState(
      patch({
        feedbackEntries: patch({
          [key]: patch({
            text: payload.text,
            email: payload.email,
          }),
        }),
      })
    );
  }
}
